import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Image } from 'react-bootstrap';
import { allAPiServicesCall } from '../../services/apiServices';
import { getAuthConfig, getFormDataConfig } from '../../services/apiUtils';
import { toast } from 'react-toastify';
import { extractObjectId } from '../../utils/utils';

const AddBannerModal = ({ show, handleClose, call }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redirectionLink, setRedirectionLink] = useState('');
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState("");
  const [newsList, setNewsList] = useState([]);
  const [selectedNewsArticle, setSelectedNewsArticle] = useState("");

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await allAPiServicesCall.getGenres({}, getAuthConfig());
        if (response.data) {
          setGenres(response.data);
        }
      } catch (error) {
        console.error('Error fetching genres:', error);
      }
    };
    const fetchNews = async () => {
      try {
        const response = await allAPiServicesCall.getNews({}, getAuthConfig());
        if (response.data) {
          setNewsList(response.data);
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      }
    };
    fetchGenres();
    fetchNews();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNewsArticleChange = (e) => {
    const newsId = e.target.value;
    setSelectedNewsArticle(newsId);

    if (newsId) {
      // Find the selected news article
      const selectedNews = newsList.find(news => extractObjectId(news._id) === newsId);
      if (selectedNews) {
        // Auto-populate fields from news article
        setTitle(selectedNews.title || '');
        setDescription(selectedNews.description || '');
        setPreview(selectedNews.coverImg || null);
        // Set redirection link to the news article URL
        setRedirectionLink(`https://lyricsweb.com/news/${extractObjectId(selectedNews._id)}`);
        // Set genre if news has one
        if (selectedNews.genre) {
          setSelectedGenre(selectedNews.genre);
        }
      }
    }
  };

  const handleSubmit = async () => {
    let imagepath = preview; // Use preview (which could be from news or uploaded image)

    if (!title || !description || !redirectionLink || !selectedGenre) {
      alert('Please fill in all required fields and select a genre.');
      return;
    }

    // Only upload if a new image file was selected
    if (image) {
      const formData = new FormData();
      formData.append('image', image);
      const response = await allAPiServicesCall.uploadBannerImage(formData, getFormDataConfig());
      if(response.data) imagepath = response.data.path;
    }

    // If no image provided and no preview, show error
    if (!imagepath) {
      alert('Please provide an image either by selecting a news article or uploading one.');
      return;
    }

    const apiPayload = {
      title,
      description,
      image: imagepath,
      redirection_link: redirectionLink,
      genre: selectedGenre,
      newsArticleId: selectedNewsArticle || undefined
    };

    setLoading(true);

    try {
      const response = await allAPiServicesCall.addBanner(getAuthConfig(), apiPayload);
      if (response) {
        toast.success(response.message);
        call();
        setTitle('');
        setDescription('');
        setImage(null);
        setPreview(null);
        setSelectedGenre('');
        handleClose();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add New Banner</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className='fw-bold'>Select News Article</Form.Label>
            <Form.Select
              value={selectedNewsArticle}
              onChange={handleNewsArticleChange}
            >
              <option value="">Select a news article to auto-fill banner</option>
              {newsList.map((news) => (
                <option key={news._id} value={news._id}>
                  {news.title}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Selecting a news article will automatically populate all fields below
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className='fw-bold'>Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter banner title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className='fw-bold'>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter banner description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className='fw-bold'>Upload Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {preview && (
              <div className="mt-3">
                <strong>Preview:</strong>
                <Image src={preview} alt="Preview" fluid className="mt-2" />
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className='fw-bold'>Redirection Link</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter redirection link"
              value={redirectionLink}
              onChange={(e) => setRedirectionLink(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className='fw-bold'>Genre</Form.Label>
            <Form.Select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
            >
              <option value="">Select a genre</option>
              {genres.map((genre) => (
                <option key={genre._id} value={genre.genreName}>
                  {genre.genreName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' style={{ width: "1rem", height: "1rem" }}></span>
            ) : 'Submit'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddBannerModal;
